import { CustomRoutes, Resource } from 'ra-core';
import { Route } from 'react-router';
import { Admin } from '@/components/admin/admin';
import { fournisseurDeDonnees } from '../fournisseurs/donnees';
import { fournisseurDAuthentification } from '../fournisseurs/authentification';
import { fournisseurDeLangue } from '../fournisseurs/langue';
import { Cadre } from './Cadre';
import { RenvoiVersLaConnexion } from './RenvoiVersLaConnexion';
import {
  ListeDesStructures,
  FicheDeStructure,
  CreationDeStructure,
  EditionDeStructure,
} from '../ressources/structures';
import {
  ListeDesComptes,
  FicheDeCompte,
  CreationDeCompte,
  EditionDeCompte,
} from '../ressources/comptes';
import { ListeDesCours } from '../ressources/cours';
import { EcranDesReglages } from '../ressources/reglages';
import { ChiffresDuneStructure, ChiffresDuParc } from '../ressources/chiffres';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * **LA RACINE DE LA SURFACE — SIX ÉCRANS, ET `CDC-5` §2 EN COMPTE SIX.**
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * §2 : « la surface d'administration, **ses six écrans**, ses vingt et une ressources, ses deux
 * rôles. » Les voici, et la répartition entre ressources et routes libres n'est pas arbitraire :
 *
 * | Écran | Monté comme | Pourquoi |
 * |---|---|---|
 * | les structures | `<Resource>` | liste, fiche, création, édition — du CRUD, et §7 dit qu'un back-office en est |
 * | les comptes | `<Resource>` | liste, fiche, **création** — F-03 et F-60 réunis, D-108 —, édition |
 * | les cours | `<Resource>` | liste seule : `CDC-5` n'ouvre aucune fiche de cours |
 * | les réglages | **route libre** | F-65 : « **un seul écran** », et il n'a ni liste ni fiche |
 * | les chiffres ouverts | **route libre** | F-67 : ce n'est pas une ressource, c'est un calcul |
 * | les chiffres réservés | **route libre** | idem, et « **parc entier, jamais par structure** » |
 *
 * **Les exports et l'import ne sont pas un septième écran** : F-66 les rend « sur la fiche de la
 * structure », et F-58 dit la même chose des colonnes d'affichage — « **ce n'est pas un écran à
 * part, c'est une partie de la fiche d'une structure** ». Trois blocs sur une fiche, pas trois
 * pages.
 *
 * **Aucun tableau de bord.** F-55 : « **aucun tableau de bord, aucun chiffre en tête de
 * surface.** Les chiffres vivent à leur écran. » `<Admin>` n'a donc pas de `dashboard`, et
 * l'écran d'arrivée est celui de la première ressource — asymétrique par rôle, ce que la matrice
 * produit d'elle-même : la liste des structures est fermée à l'`orgadmin`.
 *
 * ───────────────────────────────────────────────────────────────────────────
 * **CE QUE CETTE RACINE REMPLACE DANS LA PEAU, ET POURQUOI.**
 * ───────────────────────────────────────────────────────────────────────────
 *
 * **`loginPage`** — la peau en livre une complète, et F-55 l'interdit : cette surface « s'atteint
 * par la page de connexion **unique** du site vitrine ». Remplacée par un renvoi.
 *
 * **`layout`** — pour porter le sélecteur permanent de F-55 en tête de la surface, sans dupliquer
 * une ligne du `Layout` recopié.
 *
 * **`i18nProvider`** — le fournisseur recopié monte `ra-language-english`. Le produit est
 * francophone de bout en bout.
 *
 * **`disableTelemetry` — ET CELUI-LÀ N'EST PAS COSMÉTIQUE.** `admin.tsx`, dans les 117 fichiers
 * recopiés, fabrique en production une `Image` vers
 * `shadcn-admin-kit-telemetry.marmelab.com` **portant le nom d'hôte de la page**. Sur une
 * surface interne, c'est un appel sortant que personne n'a demandé, et le nom d'hôte d'un
 * back-office n'a rien à faire chez un tiers. **Deux verrous, et le second ne dépend pas de
 * moi** : ce drapeau, et la politique de sécurité de contenu de `nginx.conf.template`, dont
 * `img-src` ne porte que `self`, `data:`, `blob:` et le domaine de contenu.
 *
 * **`requireAuth`** — sans lui, `ra-core` laisse s'afficher une coquille avant de savoir qui
 * regarde. Sur cette surface, la garde doit précéder l'écran : la matrice décide de tout, et
 * elle a besoin du rôle.
 */
export function Administration() {
  return (
    <Admin
      dataProvider={fournisseurDeDonnees}
      authProvider={fournisseurDAuthentification}
      i18nProvider={fournisseurDeLangue}
      layout={Cadre}
      loginPage={RenvoiVersLaConnexion}
      requireAuth
      disableTelemetry
      title="Swype — administration"
    >
      {/**
       * **Les structures.** `list` est fermé à l'`orgadmin` par la matrice, donc la peau ne lui
       * montre ni l'entrée de menu ni la page — F-55, « l'écran d'arrivée est asymétrique par
       * rôle ». Et **aucun `delete` n'est déclaré** : F-56 (Q543), « il n'existe aucun geste de
       * suppression d'une structure ».
       */}
      <Resource
        name="structures"
        list={ListeDesStructures}
        show={FicheDeStructure}
        create={CreationDeStructure}
        edit={EditionDeStructure}
        options={{ label: 'Structures' }}
      />

      {/**
       * **Les comptes.** Portés par la structure courante : les écrans exigent qu'elle soit
       * choisie plutôt que de rendre une liste du parc — F-55 critère 2.
       *
       * **`create` monte l'écran qui crée un compte ET le rattache**, F-03 et F-60 — la
       * condition que D-108 pose : « les deux routes se câblent ensemble, sur un seul écran,
       * parce qu'un compte créé sans rattachement n'entre dans aucune structure ». Il est du
       * `superadmin` seul, §6.14 accordant « **NON** » à l'`orgadmin` sur cette colonne.
       *
       * **Et `hasCreate` de `ra-core` ne consulte AUCUN droit** : `Resource.registerResource`
       * rend `hasCreate: !!create`, donc le `CreateButton` par défaut de la peau paraîtrait à
       * l'`orgadmin`, qui voit la liste. C'est `ListeDesComptes` qui le garde, par
       * `useCanAccess` — « un bouton qui mène à un 403 » est exactement la confusion que F-55
       * refuse. La ressource `structures` n'avait pas ce besoin : sa liste elle-même est fermée
       * à l'`orgadmin`.
       */}
      <Resource
        name="comptes"
        list={ListeDesComptes}
        show={FicheDeCompte}
        create={CreationDeCompte}
        edit={EditionDeCompte}
        options={{ label: 'Comptes' }}
      />

      {/**
       * **Les cours.** Liste seule, et c'est le cahier des charges : `CDC-5` n'ouvre aucune fiche
       * de cours. F-62 — « **la liste des cours est l'écran** » — et les gestes de F-63 sont des
       * gestes **sur la ligne**, pas une page de détail.
       */}
      <Resource name="cours" list={ListeDesCours} options={{ label: 'Cours' }} />

      <CustomRoutes>
        {/* F-65 : un seul écran porte les cinq réglages, plus le bloc du parc. */}
        <Route path="/reglages" element={<EcranDesReglages />} />
        {/* F-67 : deux écrans de chiffres, et deux seulement. */}
        <Route path="/chiffres" element={<ChiffresDuneStructure />} />
        <Route path="/chiffres-du-parc" element={<ChiffresDuParc />} />
      </CustomRoutes>
    </Admin>
  );
}
